import type { Category, Product } from '@scayle/storefront-nuxt'
import { useDebounceFn } from '@vueuse/core'
import { useSearch } from '@scayle/storefront-search/composables'

import {
  DEBOUNCED_SEARCH_DURATION,
  DIRECT_ORDER_EXCLUDE_FILTER,
  EXCLUDED_FROM_SEARCH_ATTRIBUTE_KEY,
  EXCLUDED_FROM_SEARCH_VALUE_ID,
  MIN_CHARS_FOR_SEARCH,
  PRODUCT_LIMIT,
  SEARCH_WITH_PARAMS,
} from '../constants/search'
import { isDirectOrder, isExcludedFromSearch } from '~/utils/product'

const useSearchResults = () => {
  const { trackNoSearchSuggestion } = useTrackingEvents()

  const {
    searchQuery,
    status: searchStatus,
    products: productSuggestions,
    categories: categorySuggestions,
    resetSearch,
    getSearchSuggestions,
  } = useSearch({ with: SEARCH_WITH_PARAMS })

  const productParams = ref()
  const {
    data,
    status: productsStatus,
    refresh: fetchProducts,
  } = useProducts(
    {
      options: { immediate: false, lazy: false },
      params: productParams,
    },
    'useSearchResults',
  )

  const isStarted = ref(false)
  const categories = ref<Category[]>([])
  const products = ref<Product[]>([])
  let requestCount = 0

  const searchFor = async (term: string) => {
    productParams.value = {
      path: '/',
      perPage: PRODUCT_LIMIT,
      where: {
        term,
        whitelistAttributes: [
          {
            type: 'attributes:not',
            key: EXCLUDED_FROM_SEARCH_ATTRIBUTE_KEY,
            values: [EXCLUDED_FROM_SEARCH_VALUE_ID],
          },
          // ECOM-2639: keep direct-order products out of suggestions.
          DIRECT_ORDER_EXCLUDE_FILTER,
        ],
      },
      with: SEARCH_WITH_PARAMS?.products,
    }
    const currentRequest = ++requestCount

    await Promise.all([getSearchSuggestions(), fetchProducts()])

    if (currentRequest !== requestCount) {
      return // Ignore outdated requests
    }

    categories.value =
      categorySuggestions.value
        ?.map((suggestion) => suggestion.categorySuggestion.category)
        .filter((category) => {
          if (/\(S\)\s*$/i.test(category.name ?? '')) return false
          const parentName = (
            category.parent?.parent?.name ||
            category.parent?.name ||
            ''
          ).trim()
          return parentName.toUpperCase() !== 'S'
        })
        .slice(0, 3) || []

    // The product shown for an exact suggestion comes from getSearchSuggestions,
    // whose v2 endpoint has no product filter param — so DIRECT_ORDER_EXCLUDE_FILTER
    // cannot be applied there, and the suggestion payload can't be trusted to
    // carry `directOrder`/`isExcludedFromSearch` for a client-side guard. Render
    // from the server-filtered useProducts results instead (they already exclude
    // direct-order/excluded products and carry full attributes), and use the
    // suggestion only to pick which product to surface by id.
    const filteredProducts =
      data.value?.products?.filter(
        (product) => !isExcludedFromSearch(product) && !isDirectOrder(product),
      ) || []

    const exactProductId =
      productSuggestions.value?.[0]?.productSuggestion.product?.id
    const exactProduct = exactProductId
      ? filteredProducts.find((product) => product.id === exactProductId)
      : undefined

    products.value = exactProduct ? [exactProduct] : filteredProducts

    if (categories.value.length === 0 && products.value.length === 0) {
      trackNoSearchSuggestion({ originalSearchTerm: term })
    }

    isStarted.value = false
  }

  const debouncedSearch = useDebounceFn(searchFor, DEBOUNCED_SEARCH_DURATION)

  watch(searchQuery, () => {
    const { value: query = '' } = searchQuery

    if (!searchQuery.value) {
      isStarted.value = false
      searchQuery.value = ''
      categories.value = []
      products.value = []
      resetSearch()
    } else if (searchQuery.value.length < MIN_CHARS_FOR_SEARCH) {
      isStarted.value = false
    } else {
      isStarted.value = true
      debouncedSearch(query)
    }
  })

  const hasSearchQuery = computed(
    () => searchQuery.value.length >= MIN_CHARS_FOR_SEARCH,
  )

  const count = computed(() => products.value.length + categories.value.length)

  return {
    count,
    hasSearchQuery,
    products,
    categories,
    fetching: computed(
      () =>
        isStarted.value ||
        productsStatus.value === 'pending' ||
        searchStatus.value === 'pending',
    ),
    resetSearch,
    searchQuery,
  }
}

let singleton: ReturnType<typeof useSearchResults>

export default () => {
  if (singleton && import.meta.client) {
    return singleton
  }
  singleton = useSearchResults()
  return singleton
}
